import { useCallback, useContext, useMemo, useState } from "react";
import { uniqBy } from "lodash";
import { DEFAULT_SAVED_VIEW_TILE_LAYER, OBJECT_TYPE } from "common/constants";
import {
  arrayHasAtLeastTwoItems,
  arrayHasExactlyOneItem,
} from "common/utils/arrays";
import { formatCoordinates } from "common/utils/coordinates";
import { getInteractiveLayerIds as getInteractiveCustomMapLayerIds } from "common-client/utils/customMapLayer";
import { LayerContext } from "../layers";
import {
  CLICK_OBJECT_MENU_ITEMS_MAX_HEIGHT,
  CLICK_OBJECT_MENU_WIDTH,
} from "../layers/__styles__/clickObjectMenu";
import ClickObjectMenuLayer, {
  ClickMenuDisplayConfig,
  MenuCategory,
  ObjectDisplayProps,
} from "../layers/clickObjectMenu";
import { CUSTOM_MAPS_SOURCE_ID_PREFIX } from "../layers/customMap";
import { id as parcelLayerId } from "../layers/parcels";
import {
  interactiveLayerIds as propertyLayerIds,
  PROPERTIES_SOURCE_ID_PREFIX,
} from "../layers/properties";
import { viewsLayerId } from "../layers/savedViews";
import { useMapScrollIsolation } from "../utils/useMapScrollIsolation";
import { CustomMapLayerConfig } from "./customMapLayer";
import {
  getInteractiveLayerIds as getInteractiveSavedViewLayerIds,
  SavedViewsLayerConfig,
} from "./savedViewsLayer";
import {
  EventFeature,
  findRelevantFeatures,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
  MapEvent,
} from "./types";

export type ClickObjectMenuLayerConfig = LayerConfig & {
  maps: CustomMapLayerConfig["maps"];
  savedViews: SavedViewsLayerConfig["value"];
};

type PausedClickInfo = {
  globalOnClick: (event: MapEvent) => void;
  event: MapEvent;
};

type ExtractPropsArgs = {
  feature: EventFeature;
  allFeatures: EventFeature[];
  pausedClickInfo: PausedClickInfo | null;
  closeMenu: () => void;
};

export const PROPERTY_HEADER = "Property";
export const PARCEL_HEADER = "Parcel";
const UNKNOWN_LABEL = "Unknown";

const extractPropertyFeatureProps = ({
  feature,
  allFeatures,
  pausedClickInfo,
  closeMenu,
}: ExtractPropsArgs): ObjectDisplayProps => {
  const parcelFeature = allFeatures.find(({ layer }) => layer.id === "parcels");
  return {
    id: feature.properties.propertyId,
    label:
      feature.properties.address ||
      formatCoordinates({
        latitude: feature.geometry.coordinates[1],
        longitude: feature.geometry.coordinates[0],
      }),
    onClick: () => {
      if (pausedClickInfo) {
        pausedClickInfo.globalOnClick(
          Object.assign(pausedClickInfo.event, {
            features: [feature, ...(parcelFeature ? [parcelFeature] : [])],
          }),
        );
      }
      closeMenu();
    },
  };
};

const extractParcelFeatureProps = ({
  feature,
  pausedClickInfo,
  closeMenu,
}: Omit<ExtractPropsArgs, "allFeatures">): ObjectDisplayProps => {
  return {
    id: feature.properties.id,
    label:
      feature.properties.address ||
      feature.properties.parcelNumber ||
      UNKNOWN_LABEL,
    onClick: () => {
      if (pausedClickInfo) {
        pausedClickInfo.globalOnClick(
          Object.assign(pausedClickInfo.event, {
            features: [feature],
          }),
        );
      }
      closeMenu();
    },
  };
};

const extractCustomMapGeometryFeatureProps = ({
  feature,
  pausedClickInfo,
  closeMenu,
}: Omit<ExtractPropsArgs, "allFeatures">): ObjectDisplayProps => {
  return {
    id: feature.properties.id,
    label: feature.properties.label,
    onClick: () => {
      if (pausedClickInfo) {
        pausedClickInfo.globalOnClick(
          Object.assign(pausedClickInfo.event, {
            features: [feature],
          }),
        );
      }
      closeMenu();
    },
  };
};

const extractUnknownFeatureProps = ({
  feature,
  closeMenu,
}: Pick<ExtractPropsArgs, "feature" | "closeMenu">): ObjectDisplayProps => ({
  id: feature.properties.id,
  label: feature.properties.id,
  onClick: () => {
    closeMenu();
  },
});

const SpecialCaseCategories = {
  [PROPERTY_HEADER]: 0,
  [PARCEL_HEADER]: 1,
} as const;

const menuCategoryHeaderComparator = (a: string, b: string) => {
  if (a in SpecialCaseCategories && b in SpecialCaseCategories) {
    return (
      SpecialCaseCategories[a as keyof typeof SpecialCaseCategories] -
      SpecialCaseCategories[b as keyof typeof SpecialCaseCategories]
    );
  } else if (a in SpecialCaseCategories) {
    return -1;
  } else if (b in SpecialCaseCategories) {
    return 1;
  } else {
    return a.localeCompare(b);
  }
};

export const categorizeMenuFeaturesHelper = ({
  allFeatures,
  pausedClickInfo,
  sourceIdToName,
  closeMenu,
}: Omit<ExtractPropsArgs, "feature" | "updateUrl"> & {
  sourceIdToName: Record<string, string>;
}): MenuCategory[] => {
  const categories: Record<string, ObjectDisplayProps[]> = {};
  for (const feature of allFeatures) {
    const source = feature.source as string;
    if (
      source.startsWith(PROPERTIES_SOURCE_ID_PREFIX) ||
      (source.startsWith(viewsLayerId) &&
        feature.sourceLayer === DEFAULT_SAVED_VIEW_TILE_LAYER)
    ) {
      const featureProps = extractPropertyFeatureProps({
        feature,
        allFeatures,
        pausedClickInfo,
        closeMenu,
      });
      categories[PROPERTY_HEADER] ??= [];
      categories[PROPERTY_HEADER]!.push(featureProps);
    } else if (source.startsWith(parcelLayerId)) {
      const featureProps = extractParcelFeatureProps({
        feature,
        pausedClickInfo,
        closeMenu,
      });
      categories[PARCEL_HEADER] ??= [];
      categories[PARCEL_HEADER]!.push(featureProps);
    } else if (
      source.startsWith(CUSTOM_MAPS_SOURCE_ID_PREFIX) ||
      (source.startsWith(viewsLayerId) &&
        feature.sourceLayer === OBJECT_TYPE.CUSTOM_MAP_GEOMETRY)
    ) {
      const mapName =
        sourceIdToName[source] ??
        ("customMapName" in feature.properties ?
          feature.properties.customMapName
        : "Unknown Map");
      const featureProps = extractCustomMapGeometryFeatureProps({
        feature,
        pausedClickInfo,
        closeMenu,
      });
      categories[mapName] ??= [];
      categories[mapName]!.push(featureProps);
    } else {
      const featureProps = extractUnknownFeatureProps({ feature, closeMenu });
      categories[UNKNOWN_LABEL] ??= [];
      categories[UNKNOWN_LABEL]!.push(featureProps);
    }
  }

  // if both parcels and properties are present, only show properties
  if (PROPERTY_HEADER in categories && PARCEL_HEADER in categories) {
    delete categories[PARCEL_HEADER];
  }

  return Object.keys(categories)
    .sort(menuCategoryHeaderComparator)
    .map(key => ({
      header: key,
      objects: categories[key]!.sort((a, b) => a.label.localeCompare(b.label)),
    }));
};

const calculateMenuHeight = (menuCategories: MenuCategory[]) => {
  const headerHeight = 33;
  const menuItemsTopPadding = 8;
  const categoryHeaderHeight = 16;
  const objectDisplayHeight = 24;
  const categoryBottomPadding = 8;
  return Math.min(
    CLICK_OBJECT_MENU_ITEMS_MAX_HEIGHT,
    headerHeight +
      menuItemsTopPadding +
      menuCategories.reduce((currentSum, category) => {
        return (
          currentSum +
          categoryHeaderHeight +
          objectDisplayHeight * category.objects.length +
          categoryBottomPadding
        );
      }, 0),
  );
};

const useClickObjectMenuHook: LayerHookBuilder<ClickObjectMenuLayerConfig> = ({
  config,
  helpers,
}) => {
  const [menuDisplayConfig, setMenuDisplayConfig] =
    useState<ClickMenuDisplayConfig | null>(null);

  const [time, setTime] = useState<number>(Date.now());
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);

  const { reset: resetScrollIsolation, ...scrollIsolationProps } =
    useMapScrollIsolation(helpers.getMap);

  const closeMenu = useCallback(
    (features?: EventFeature[], event?: MapEvent) => {
      resetScrollIsolation();
      if (features?.length && event) {
        helpers.setCursor("pointer");
        // set hover state for all features
        features.map(feat =>
          helpers.emit({
            type: "setFeatureState",
            data: {
              field: "hover",
              source: feat.source,
              sourceLayer: feat.sourceLayer,
              id: feat.properties.id ?? feat.properties.propertyId,
            },
          }),
        );
        // find top feature to use for tooltip text
        const categorizedFeatures = categorizeMenuFeatures(features, null);
        helpers.emit({
          type: "setTooltip",
          data: {
            text: categorizedFeatures[0]!.objects[0]!.label,
            x: event.point.x,
            y: event.point.y,
          },
        });
      } else {
        helpers.setCursor("grab");
      }
      setMenuDisplayConfig(null);
      setMenuCategories([]);
    },
    [setMenuDisplayConfig, setMenuCategories, helpers],
  );

  const suppressHoverBehavior = useCallback(
    (features: EventFeature[]) => {
      helpers.setCursor("unset");
      helpers.emit({ type: "removeTooltip", data: {} });
      features.map(feat =>
        helpers.emit({
          type: "removeFeatureState",
          data: {
            field: "hover",
            source: feat.source,
            sourceLayer: feat.sourceLayer,
            id: feat.properties.id ?? feat.properties.propertyId,
          },
        }),
      );
    },
    [helpers],
  );

  const { isLayerVisible, visibleLayerIdsForGroup, approximateBfeToolState } =
    useContext(LayerContext);

  const allInteractiveLayerIds = getInteractiveCustomMapLayerIds(
    config.maps,
    isLayerVisible,
  ).concat(
    getInteractiveSavedViewLayerIds(visibleLayerIdsForGroup("savedViews")),
    parcelLayerId,
    propertyLayerIds,
  );

  const sourceIdToName = useMemo(() => {
    const lookup: Record<string, string> = {};
    config.maps.forEach(map => {
      map.sources.forEach(source => {
        lookup[source.sourceId] = map.name;
      });
    });
    return lookup;
  }, [config.maps]);

  const categorizeMenuFeatures = useCallback(
    (allFeatures: EventFeature[], clickInfo: PausedClickInfo | null) =>
      categorizeMenuFeaturesHelper({
        allFeatures,
        pausedClickInfo: clickInfo,
        sourceIdToName,
        closeMenu,
      }),
    [sourceIdToName, closeMenu, helpers],
  );

  return useMemo<LayerHook>(() => {
    return {
      // -2 so that the markers show above the PIP's marker
      zIndex: -2,
      render: () => {
        return (
          <ClickObjectMenuLayer
            key={"click-object-menu"}
            menuDisplayConfig={menuDisplayConfig}
            time={time}
            menuCategories={menuCategories}
            closeMenu={closeMenu}
            {...scrollIsolationProps}
          />
        );
      },
      canHandleHover: () => !!config.interactive?.hover && !!menuDisplayConfig,
      onHover: ({ event }) => {
        if (menuDisplayConfig) {
          const features = uniqBy(
            findRelevantFeatures({
              event,
              layerId: allInteractiveLayerIds as LayerId[],
            }),
            "id",
          );
          // suppress hover states and tooltip while menu opens
          suppressHoverBehavior(features);
          return true;
        }
        return false;
      },
      canHandleClick: event => {
        return (
          !!config.interactive?.click &&
          (!!menuDisplayConfig || (event.features?.length ?? 0) > 1)
        );
      },
      onClick: ({ event, globalOnClick }) => {
        // don't go through the menu if we're in the BFE Tool
        if (approximateBfeToolState?.mode === "on") {
          return false;
        }

        const features = uniqBy(
          findRelevantFeatures({
            event,
            layerId: allInteractiveLayerIds as LayerId[],
          }),
          "id",
        );

        if (menuDisplayConfig) {
          // re-add hover states and tooltip after menu closes
          closeMenu(features, event);
          return true;
        } else if (features.length > 1) {
          // dedupe properties and parcels
          const newMenuCategories = categorizeMenuFeatures(features, {
            event,
            globalOnClick,
          });
          // we only open the menu if there are multiple categories,
          // or if there are multiple objects in the single category
          if (
            !(
              arrayHasAtLeastTwoItems(newMenuCategories) ||
              (arrayHasExactlyOneItem(newMenuCategories) &&
                newMenuCategories[0].objects.length > 1)
            )
          ) {
            return false;
          }
          const menuHeight = calculateMenuHeight(newMenuCategories);

          // remove hover states and tooltip when menu opens
          suppressHoverBehavior(features);
          setMenuCategories(newMenuCategories);
          setMenuDisplayConfig({
            longitude: event.lngLat.lng,
            latitude: event.lngLat.lat,
            displayOnLeft:
              event.point.x >
              window.innerWidth - (CLICK_OBJECT_MENU_WIDTH + 100),
            displayAbove:
              event.point.y > window.innerHeight - (100 + menuHeight),
          });
          setTime(Date.now());
          return true;
        }

        return false;
      },
    };
  }, [
    menuDisplayConfig,
    time,
    menuCategories,
    allInteractiveLayerIds,
    scrollIsolationProps.onMouseEnter,
    scrollIsolationProps.onMouseLeave,
  ]);
};

export default useClickObjectMenuHook;
